// src/utils/canonicalUrl.ts - Canonical URL utilities
export class CanonicalUrlManager {
  private static readonly BASE_URL = import.meta.env.VITE_NODE_ENV === 'production' 
    ? 'https://strymhub.com' 
    : 'http://localhost:5173';
  
  /**
   * Generate canonical URL for a page
   */
  static generateCanonical(path: string, params?: Record<string, string>): string {
    // Clean path
    let cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // Remove trailing slash except for root
    if (cleanPath !== '/' && cleanPath.endsWith('/')) {
      cleanPath = cleanPath.slice(0, -1);
    }
    
    let canonical = `${this.BASE_URL}${cleanPath}`;
    
    // Add parameters if provided
    if (params && Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams(params);
      canonical += `?${searchParams.toString()}`;
    }
    
    return canonical;
  }
  
  /**
   * Set canonical URL in document head
   */
  static setCanonical(url: string): void {
    // Remove existing canonical links
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    // Add new canonical link
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = url;
    document.head.appendChild(link);
  }
  
  /**
   * Get current page canonical URL
   */
  static getCurrentCanonical(): string {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    
    // Remove tracking parameters that shouldn't be in canonical URLs
    const trackingParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid'];
    trackingParams.forEach(param => searchParams.delete(param));
    
    const cleanParams: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      cleanParams[key] = value;
    });
    
    return this.generateCanonical(path, Object.keys(cleanParams).length > 0 ? cleanParams : undefined);
  }
  
  /**
   * Check if current URL needs redirect for canonical purposes
   */
  static needsCanonicalRedirect(): { needsRedirect: boolean; canonicalUrl?: string } {
    const current = window.location.href;
    const canonical = this.getCurrentCanonical();
    
    // Check for common non-canonical patterns
    const currentUrl = new URL(current);
    
    // Check for www subdomain
    if (currentUrl.hostname.startsWith('www.')) {
      return {
        needsRedirect: true,
        canonicalUrl: canonical
      };
    }
    
    // Check for trailing slash on non-root paths
    if (currentUrl.pathname !== '/' && currentUrl.pathname.endsWith('/')) {
      return {
        needsRedirect: true,
        canonicalUrl: canonical
      };
    }
    
    // Check for HTTP vs HTTPS
    if (currentUrl.protocol === 'http:' && this.BASE_URL.startsWith('https:')) {
      return {
        needsRedirect: true,
        canonicalUrl: canonical
      };
    }
    
    return { needsRedirect: false };
  }
  
  /**
   * Perform canonical redirect if needed (client-side)
   */
  static performCanonicalRedirect(): boolean {
    const { needsRedirect, canonicalUrl } = this.needsCanonicalRedirect();
    
    if (needsRedirect && canonicalUrl && canonicalUrl !== window.location.href) {
      // Use replace to avoid adding to history
      window.location.replace(canonicalUrl);
      return true;
    }
    
    return false;
  }
  
  /**
   * Generate Open Graph URL (always canonical)
   */
  static generateOgUrl(path: string): string {
    return this.generateCanonical(path);
  }
  
  /**
   * Generate Twitter Card URL (always canonical)
   */
  static generateTwitterUrl(path: string): string {
    return this.generateCanonical(path);
  }
}

export default CanonicalUrlManager;
